<template>
  <article class="card">
    <b-modal size="sm" title="Remove Project" ref="remove">
      <div>삭제하시겠습니까?</div>
      <template slot="modal-footer">
        <b-btn variant="secondary" @click="closeRemoveModal">닫기</b-btn>
        <b-btn variant="primary" @click="remove">삭제</b-btn>
      </template>
    </b-modal>
    <router-link :to="`/artwork/${artwork.id}/`" rel="bookmark">
      <div class="background" />
      <div class="modify-button-group" v-if="artwork.author.id === user.id">
        <b-btn class="btn-circle btn-white" @click.prevent="showRemoveModal">
          <font-awesome-icon :icon="['fa', 'trash']" color="black" />
        </b-btn>
        <b-btn class="btn-circle btn-white" @click.prevent="edit">
          <font-awesome-icon :icon="['fa', 'user-edit']" color="black" />
        </b-btn>
      </div>
      <figure>
        <template v-if="fixedLayout">
          <div
            :style="{ backgroundColor: artwork.color.code }"
            v-show="!croppedCoverLoaded"
          >
            <img
              src="../assets/dummy.png"
              class="placeholder img-fluid"
              style="visibility: hidden"
            />
          </div>
          <img
            :src="`${artwork.cropped_cover}?width=480`"
            class="card-img-top"
            @load="croppedCoverLoaded = true"
            v-show="croppedCoverLoaded"
          />
        </template>
        <template v-else>
          <div
            :style="{ backgroundColor: artwork.color.code }"
            v-show="!coverLoaded"
          >
            <img
              src="../assets/dummy.png"
              class="placeholder img-fluid"
              style="visibility: hidden"
            />
          </div>
          <img
            :src="`${artwork.cover}?width=480`"
            class="card-img-top"
            @load="coverLoaded = true"
            v-show="coverLoaded"
          />
        </template>
      </figure>
    </router-link>
    <router-link :to="`/artwork/${artwork.id}/`" rel="bookmark">
      <b-card-body>
        <p
          class="selection"
          v-if="
            invite && invite.id && (invite.owner === user.id || user.is_staff)
          "
        >
          <b-btn
            class="mr-1"
            variant="red-hover"
            size="sm"
            @click.prevent="select(artwork)"
            v-if="artwork.application.selected"
          >
            Selected
          </b-btn>
          <b-btn
            class="mr-1"
            variant="white-hover"
            size="sm"
            @click.prevent="select(artwork)"
            v-else
          >
            Select
          </b-btn>
        </p>
        <p class="levels" v-if="user.is_staff">
          <b-button
            :key="level"
            class="mr-1"
            :disabled="artwork.level === level"
            size="sm"
            @click.prevent="setArtworkLevel(artwork, level)"
            v-for="level in [5, 4, 3, 2, 1, null]"
            >{{ level || "X" }}</b-button
          >
        </p>
        <div
          class="artwork-button-group d-flex flex-column justify-content-between align-items-end"
        >
          <b-btn variant="link" :id="`share-${artwork.id}`" @click.prevent>
            <font-awesome-icon :icon="['fa', 'share-alt']" color="black" />
          </b-btn>
          <b-popover
            :target="`share-${artwork.id}`"
            placement="auto"
            triggers="click blur"
          >
            <div class="d-flex social">
              <a :href="getLink(artwork, 'facebook')" target="_blank">
                <img src="../assets/facebook_color.png" class="img-fluid" />
              </a>
              <a :href="getLink(artwork, 'twitter')" target="_blank">
                <img src="../assets/twitter_color.png" class="img-fluid" />
              </a>
              <a :href="getLink(artwork, 'pinterest')" target="_blank">
                <img src="../assets/pinterest_color.png" class="img-fluid" />
              </a>
              <div>
                <b-btn
                  variant="link"
                  class="h-100 w-100"
                  @click="copyLink(artwork)"
                >
                  <font-awesome-icon
                    :icon="['fa', 'link']"
                    color="white"
                    size="lg"
                  />
                </b-btn>
              </div>
            </div>
            <div>
              <b-input
                :value="getUrl(artwork)"
                size="sm"
                readonly
                @click.native="copyLink"
                ref="url"
              ></b-input>
            </div>
          </b-popover>
          <b-btn
            variant="red-hover"
            size="sm"
            @click.prevent="collect(artwork)"
            v-if="artwork.collection"
          >
            Collected
          </b-btn>
          <b-btn
            variant="white-hover"
            size="sm"
            @click.prevent="collect(artwork)"
            v-else
          >
            Collect
          </b-btn>
        </div>

        <footer>
          <address>
            <router-link
              :to="`/${artwork.author.username}/`"
              :target="newWindow ? '_blank' : null"
              rel="author"
              class="author text-uppercase font-weight-bold"
              @click.native.stop
            >
              {{ artwork.author.name }}
            </router-link>
          </address>
        </footer>
        <p class="card-text">
          <category
            :categories="artwork.categories.slice(0, 1)"
            :new-window="newWindow"
            @click.native.stop
          />
          ━
        </p>
        <header>
          <h4 class="card-title display-4 text-ellipsis">
            {{ artwork.title }}
          </h4>
        </header>
      </b-card-body>
    </router-link>
  </article>
</template>

<script>
import Vue from "vue";
import Category from "./Category";
import { Collection, InviteApplication } from "../models";
import axios from "axios";

export default Vue.component("Artwork", {
  components: {
    Category
  },
  props: {
    artwork: Object,
    fixedLayout: {
      type: Boolean,
      default: true
    },
    invite: Object,
    selected: Boolean,
    index: Number,
    newWindow: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      user: window.user,
      coverLoaded: false,
      croppedCoverLoaded: false
    };
  },
  computed: {
    height() {
      if (this.artwork.height * this.artwork.width >= 16000000) {
        return this.artwork.height / 10;
      }

      return this.artwork.height;
    },
    width() {
      if (this.artwork.height * this.artwork.width >= 16000000) {
        return this.artwork.width / 10;
      }

      return this.artwork.width;
    }
  },
  methods: {
    edit() {
      this.$router.push({
        name: "upload-with-artwork",
        params: { artworkId: this.artwork.id }
      });
    },
    showRemoveModal() {
      this.$refs.remove.show();
    },
    remove() {
      this.$emit("remove", this.index);
      this.closeRemoveModal();
    },
    closeRemoveModal() {
      this.$refs.remove.hide();
    },
    getUrl(artwork) {
      return `${location.origin}/artwork/${artwork.id}/`;
    },
    getLink(artwork, type) {
      const url = encodeURIComponent(this.getUrl(artwork));
      const media = encodeURIComponent(`${location.origin}${artwork.cover}/`);

      if (type === "facebook") {
        return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
      }

      if (type === "twitter") {
        return `https://twitter.com/intent/tweet?url=${url}`;
      }

      if (type === "pinterest") {
        return `http://pinterest.com/pin/create/button/?url=${url}&media=${media}`;
      }

      if (type === "tumblr") {
        return `https://www.tumblr.com/widgets/share/tool?canonicalUrl=${url}`;
      }
    },
    copyLink() {
      this.$refs.url.$el.select();
      document.execCommand("copy");
    },
    async collect(artwork) {
      if (!this.user) {
        this.$router.push({
          name: "login",
          query: {
            redirect: location.hash.substr(1)
          }
        });
        return;
      }

      if (artwork.collection) {
        await new Collection({
          id: artwork.collection
        }).delete();

        this.$set(artwork, "collection", null);
      } else {
        const { id } = await new Collection({
          artwork: artwork.id
        }).save();

        this.$set(artwork, "collection", id);
      }
    },
    async select(artwork) {
      let application = new InviteApplication(artwork.application);
      application.selected = !application.selected;
      application = await application.save();
      artwork.application.selected = application.selected;

      if (this.selected && !artwork.application.selected) {
        this.$emit("remove", this.index, false);
      } else {
        this.$emit("update");
      }
    },
    async setArtworkLevel(artwork, level) {
      if (artwork.level === level) {
        level = null;
      }

      artwork.level = level;

      await axios.patch(`/api/v1/artworks/${artwork.id}/`, {
        level
      });

      this.$emit("update");
    }
  }
});
</script>

<style lang="scss" scoped>
.card {
  overflow: hidden;
  display: inline-block;
  width: calc(50vw - 12px);
  margin: 0 6px 12px;
  background-color: #f7f7f7;

  @media (min-width: 548px) {
    width: 240px;
    margin: 0 12px 40px;
  }

  a {
    color: rgb(72, 72, 72);
    background-color: #f7f7f7 !important;
  }

  .background {
    position: absolute;
    width: 100%;
    height: calc(100% - 90px);

    &:hover {
      background-color: white;
      opacity: 0.3;
    }
  }

  a {
    .modify-button-group {
      display: none;
      position: absolute;
      right: 0;
      z-index: 999;

      a,
      button {
        margin: 5px 5px 0 0;
        padding: 0;
      }
    }

    &:hover {
      .modify-button-group {
        display: block;
      }
    }
  }

  figure {
    margin: 0;
  }

  .card-body {
    position: relative;
    height: 90px;
    padding: 10px;
    font-size: 11px;
    margin: 0;

    .artwork-button-group {
      position: absolute;
      right: 0;
      padding: 0 10px 10px 10px;
      height: 80px;
    }

    .card-title,
    .card-text,
    address {
      margin: 0;
    }

    .author {
      font-size: 13px;
    }

    a,
    .card-title {
      &:hover {
        text-decoration: underline;
      }
    }
    .card-title {
      max-width: calc(100% - 52px);
      line-height: inherit;
    }
  }
}

.social {
  a {
    flex-grow: 1;
    flex-basis: 0;
    padding: 10px;
    background-color: transparent !important;

    img {
      width: 26px;
    }
  }

  div {
    flex-grow: 1;
    flex-basis: 0;
    padding: 10px;

    button {
      background-color: black;
      border-radius: 50%;
    }
  }
}

.placeholder {
  opacity: 0.7;
}

.selection {
  position: absolute;
  right: 0;
  margin-top: -68px;
}

.levels {
  position: absolute;
  right: 0;
  margin-top: -40px;
}
</style>
